import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "../lib/api";
import { format, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subWeeks, subMonths } from "date-fns";
import { es } from "date-fns/locale";
import {
  BarChart3,
  Calendar,
  TrendingUp,
  TrendingDown,
  Pause,
  Play
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Badge } from "../components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { API } from "../App";
import DashboardLayout from "../components/DashboardLayout";

const platformConfig = {
  google_ads: {
    name: "Google Ads",
    color: "bg-blue-500",
    description: "Rendimiento de campañas de Google Ads"
  },
  meta_ads: {
    name: "Meta Ads",
    color: "bg-blue-600",
    description: "Facebook e Instagram Ads"
  },
  tiktok_ads: {
    name: "TikTok Ads",
    color: "bg-slate-900",
    description: "Publicidad en TikTok"
  },
  google_analytics: {
    name: "Google Analytics",
    color: "bg-amber-500",
    description: "Datos de tráfico y conversiones"
  }
};

const PlatformView = () => {
  const { platform } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [dateRange, setDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date()
  });
  const [pendingDateRange, setPendingDateRange] = useState({
    from: subDays(new Date(), 30),
    to: new Date()
  });
  const [datePopoverOpen, setDatePopoverOpen] = useState(false);

  const config = platformConfig[platform] || {
    name: platform,
    color: "bg-slate-500",
    description: "Plataforma de publicidad"
  };

  useEffect(() => {
    fetchPlatformData();
  }, [platform, dateRange]);

  const fetchPlatformData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("auth_token");
      const response = await axios.get(`${API}/analytics/platform/${platform}`, {
        params: {
          start_date: format(dateRange.from, "yyyy-MM-dd"),
          end_date: format(dateRange.to, "yyyy-MM-dd")
        },
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setData(response.data);
    } catch (error) {
      console.error("Error fetching platform data:", error);
      toast.error("Error al cargar los datos de la plataforma");
    } finally {
      setLoading(false);
    }
  };

  const KPICard = ({ title, value, prefix = "", suffix = "" }) => (
    <Card className="bg-white border border-slate-200">
      <CardContent className="p-4">
        <p className="label-swiss">{title}</p>
        <p className="text-2xl font-black tracking-tight text-slate-900 mt-1">
          {prefix}{typeof value === 'number' ? value.toLocaleString('es-ES', { maximumFractionDigits: 2 }) : value}{suffix}
        </p>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="platform-view">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-4">
            <div className={`w-12 h-12 ${config.color} rounded-xl flex items-center justify-center`}>
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900">{config.name}</h1>
              <p className="text-slate-600">{config.description}</p>
            </div>
          </div>
          
          <Popover open={datePopoverOpen} onOpenChange={(open) => { setDatePopoverOpen(open); if (open) setPendingDateRange(dateRange); }}>
            <PopoverTrigger asChild>
              <Button variant="outline" className="border-slate-200" data-testid="platform-date-picker">
                <Calendar className="w-4 h-4 mr-2" />
                {format(dateRange.from, "d MMM", { locale: es })} - {format(dateRange.to, "d MMM yyyy", { locale: es })}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <div className="flex">
                {/* Quick date options */}
                <div className="border-r border-slate-200 p-2 space-y-1 min-w-[180px]">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider px-2 py-1">Accesos rápidos</p>
                  {[
                    { label: "Hoy", from: new Date(), to: new Date() },
                    { label: "Ayer", from: subDays(new Date(), 1), to: subDays(new Date(), 1) },
                    { label: "Últimos 7 días", from: subDays(new Date(), 6), to: new Date() },
                    { label: "Últimos 14 días", from: subDays(new Date(), 13), to: new Date() },
                    { label: "Últimos 30 días", from: subDays(new Date(), 29), to: new Date() },
                    { label: "Esta semana", from: startOfWeek(new Date(), { weekStartsOn: 1 }), to: new Date() },
                    { label: "Semana pasada", from: startOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }), to: endOfWeek(subWeeks(new Date(), 1), { weekStartsOn: 1 }) },
                    { label: "Este mes", from: startOfMonth(new Date()), to: new Date() },
                    { label: "Mes pasado", from: startOfMonth(subMonths(new Date(), 1)), to: endOfMonth(subMonths(new Date(), 1)) },
                  ].map((preset) => (
                    <button
                      key={preset.label}
                      onClick={() => {
                        const range = { from: preset.from, to: preset.to };
                        setDateRange(range);
                        setPendingDateRange(range);
                        setDatePopoverOpen(false);
                      }}
                      className="w-full text-left px-3 py-2 text-sm text-slate-700 hover:bg-slate-100 rounded-md transition-colors"
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
                {/* Calendar */}
                <div className="p-2 flex flex-col">
                  <CalendarComponent
                    mode="range"
                    selected={pendingDateRange}
                    onSelect={(range) => range && setPendingDateRange(range)}
                    numberOfMonths={2}
                    locale={es}
                  />
                  <div className="border-t border-slate-200 pt-2 mt-1 flex justify-end">
                    <Button
                      size="sm"
                      className="bg-red-600 hover:bg-red-700 text-white"
                      disabled={!pendingDateRange.from}
                      onClick={() => {
                        const range = { from: pendingDateRange.from, to: pendingDateRange.to || pendingDateRange.from };
                        setDateRange(range);
                        setPendingDateRange(range);
                        setDatePopoverOpen(false);
                      }}
                    >
                      Aplicar
                    </Button>
                  </div>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        ) : data ? (
          <>
            {/* KPI Summary */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <KPICard title="Gasto Total" value={data.total.spend} prefix="€" />
              <KPICard title="Ingresos" value={data.total.revenue} prefix="€" />
              <KPICard title="ROAS" value={data.total.roas} suffix="x" />
              <KPICard title="Conversiones" value={data.total.conversions} />
            </div>

            {/* Campaigns Table */}
            <Card className="bg-white border border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg font-bold text-slate-900">Campañas</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-slate-200">
                        <TableHead className="text-slate-600">Campaña</TableHead>
                        <TableHead className="text-slate-600">Estado</TableHead>
                        <TableHead className="text-slate-600 text-right">Gasto</TableHead>
                        <TableHead className="text-slate-600 text-right">Ingresos</TableHead>
                        <TableHead className="text-slate-600 text-right">ROAS</TableHead>
                        <TableHead className="text-slate-600 text-right">Conv.</TableHead>
                        <TableHead className="text-slate-600 text-right">CPA</TableHead>
                        <TableHead className="text-slate-600 text-right">CTR</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.campaigns.map((campaign) => (
                        <TableRow key={campaign.campaign_id} className="border-slate-100 hover:bg-slate-50">
                          <TableCell className="font-medium text-slate-900">
                            {campaign.name}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={campaign.status === 'active' ? 'default' : 'secondary'}
                              className={campaign.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}
                            >
                              {campaign.status === 'active' ? (
                                <><Play className="w-3 h-3 mr-1" /> Activa</>
                              ) : (
                                <><Pause className="w-3 h-3 mr-1" /> Pausada</>
                              )}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            €{campaign.spend.toLocaleString('es-ES')}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            €{campaign.revenue.toLocaleString('es-ES')}
                          </TableCell>
                          <TableCell className="text-right">
                            <span className={`font-mono ${campaign.roas >= 2 ? 'text-green-600' : campaign.roas < 1 ? 'text-red-600' : 'text-slate-900'}`}>
                              {campaign.roas}x
                            </span>
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {campaign.conversions}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            €{campaign.cpa.toFixed(2)}
                          </TableCell>
                          <TableCell className="text-right font-mono">
                            {campaign.ctr}%
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <div className="text-center py-12">
            <p className="text-slate-600">No hay datos disponibles para esta plataforma</p>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default PlatformView;
